import type { AnyFieldDef, StaticOptions, ViewItem } from '@resource-kit/model';
import type { EntityField } from '../contracts';

/**
 * Build the legacy-shaped field metadata still consumed by FormView and CSV
 * export. The source is entirely native: the model owns field semantics and
 * the list item owns presentation overrides.
 *
 * It sits behind `./react` rather than at the root because its RESULT type is
 * `EntityField`, one of the React contracts (`render` is a `ComponentType`) —
 * the function body itself evaluates nothing framework-bound. It was cut out of
 * `src/resource-kit/ui/columns.ts` in #2167 PR-3 because that file also builds
 * TanStack `ColumnDef`s out of host UI components, which stay in the host until
 * #2169.
 */
export function viewFieldOf<T, F extends Record<string, AnyFieldDef>>(
  item: ViewItem<T, F>,
  modelField: AnyFieldDef,
  overrides: Partial<Pick<EntityField<T>, 'required' | 'readonly'>> = {},
): EntityField<T> {
  const { options, ...field } = modelField;
  const base = {
    ...field,
    label: item.label ?? modelField.label,
    ...(item.widget !== undefined ? { widget: item.widget } : {}),
    ...(options !== undefined
      ? {
          options: {
            source: 'static',
            items: [...options],
          } satisfies StaticOptions,
        }
      : {}),
    ...overrides,
  };

  if (modelField.virtual === true) {
    return { ...base, key: item.field, virtual: true } as EntityField<T>;
  }
  return { ...base, key: item.field } as EntityField<T>;
}
